import React from 'react';
import { manhattanDistance } from '../../Map/utils';

const VehiclePathCard = ({ 
  vehicle, 
  index, 
  selectedVehicleIndex, 
  maxCapacity, 
  startId, 
  selectVehiclePath, 
  resetVehiclePath, 
  removePointFromPath,
  locations,
  undoLastPoint
}) => {
  const isSelected = selectedVehicleIndex === index;
  const customerPoints = vehicle.path.filter(p => p && p.id !== startId).length;
  const isCapacityExceeded = maxCapacity > 0 && customerPoints > maxCapacity;
  
  // Calculate total path distance
  const calculatePathDistance = () => {
    if (!vehicle.path || vehicle.path.length < 2) {
      return 0;
    }
    
    let totalDistance = 0;
    
    for (let i = 0; i < vehicle.path.length - 1; i++) {
      const current = vehicle.path[i];
      const next = vehicle.path[i + 1];
      
      if (current && next) {
        totalDistance += manhattanDistance(
          current.latitude, 
          current.longitude, 
          next.latitude, 
          next.longitude
        );
      }
    }
    
    return totalDistance;
  };
  
  // Calculate path distance
  const pathDistance = calculatePathDistance();
  
  // Create concise path representation, e.g., 1 → 5 → 6 → 7 → 1
  const createPathSequence = () => {
    if (!vehicle.path || vehicle.path.length === 0) return "";
    
    return vehicle.path
      .map(point => point ? point.name : "")
      .filter(Boolean)
      .join(" → ");
  };
  
  const pathSequence = createPathSequence();

  // Check if last point can be undone
  const canUndoLastPoint = () => {
    // If path has at least two points (start point and at least one other point)
    return vehicle.path && vehicle.path.length > 1;
  };
  
  // Handle undo last point operation
  const handleUndoLastPoint = (e) => {
    e.stopPropagation(); // Prevent event bubbling
    if (canUndoLastPoint()) {
      undoLastPoint(index);
    }
  };
  
  return (
    <div 
      className={`vehicle-path-card ${isSelected ? 'selected' : ''} ${isCapacityExceeded ? 'capacity-exceeded' : ''}`}
      onClick={() => selectVehiclePath(index)}
      style={{borderColor: vehicle.color}}
    >
      {/* Vehicle title and distance */}
      <div className="vehicle-path-header" style={{backgroundColor: vehicle.color}}>
        <span className="vehicle-number">Vehicle {index + 1}</span>
        <span className="path-distance-value">{pathDistance.toFixed(2)} km</span>
        <button 
          className="reset-vehicle-btn"
          onClick={(e) => {
            e.stopPropagation();
            resetVehiclePath(index);
          }}
          title="Reset this vehicle path"
        >
          ↺
        </button>
      </div>
      
      {/* Path sequence */}
      <div className="vehicle-path-sequence">
        {vehicle.path.length <= 1 ? (
          <span className="empty-path-msg">No path set</span>
        ) : (
          <div className="path-sequence">
            {pathSequence}
          </div>
        )}
      </div>

      {/* Undo last point button - always reserve space */}
      <div className="vehicle-path-actions">
        {canUndoLastPoint() ? (
          <button 
            className="undo-last-point-btn"
            onClick={handleUndoLastPoint}
            title="Remove the last point from this path"
          >
            <span className="undo-icon">↩</span>
            <span>Undo last point</span>
          </button>
        ) : (
          // Reserve space for consistent height
          <div className="undo-placeholder"></div>
        )}
      </div>
      
      {/* Display capacity status */}
      {maxCapacity > 0 && (
        <div className={`vehicle-path-status ${isCapacityExceeded ? 'capacity-warning' : ''}`}>
          <div className="path-count">
            <span className="customer-count">
              {customerPoints} points (excluding start)
              {isCapacityExceeded && (
                <span className="capacity-warning-badge">
                  <span className="warning-icon">⚠</span>
                  <span className="warning-text">Exceeds Max Capacity: {maxCapacity}</span>
                </span>
              )}
            </span>
            <span className="total-count">
              Total: {vehicle.path.length} points
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehiclePathCard; 